import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  QueryFunctionContext,
  InfiniteData,
} from '@tanstack/react-query'
import type { GetHomePageRecommendationsResponse } from 'cf-grpc/creativefabrica/content/v2/service_pb'
import { rootPath } from '@repo/web-utils/root-path'

import { mapToContentDeliveryLanguage } from '@web/utils/data-mapping'
import type { Language } from 'codegen/types'

export const ITEMS_PER_PAGE = 36
export const FONTS_CATEGORY = 23
export const GRAPHICS_CATEGORY = 553
export const CRAFTS_CATEGORY = 26
export const EMBROIDERY_CATEGORY = process.env.NEXT_PUBLIC_ENV === 'production' ? 735 : 706
export const BUNDLES_CATEGORY = 546

export type Category =
  | typeof FONTS_CATEGORY
  | typeof GRAPHICS_CATEGORY
  | typeof CRAFTS_CATEGORY
  | typeof EMBROIDERY_CATEGORY
  | typeof BUNDLES_CATEGORY

interface Input {
  categories?: number[]
  locale: Language
  userId?: string
}
type QData = GetHomePageRecommendationsResponse
type QKey = readonly [typeof queryPrefix, Input, 0]
type QKeyInfiniteHomepageProducts = readonly [typeof queryPrefix, Input, 'infinite']
interface PageParam {
  page: number
  pageSize: number
}

// Fetch function to call the API with query parameters (client-side only)
export async function fetchHomepageProducts({
  page,
  pageSize,
  categories,
  locale,
  userId,
}: {
  page: number
  pageSize: number
  categories?: number[]
  locale: Language
  userId?: string
}): Promise<GetHomePageRecommendationsResponse> {
  const contentLang = mapToContentDeliveryLanguage.get(locale)
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
    ...(categories && { categories: categories.join(',') }),
    ...(contentLang && { language: contentLang.toString() }),
  })
  const response = await fetch(
    `${rootPath}/api/get-homepage-recommendations/?${params.toString()}`,
    {
      headers: { 'cf-user-id': userId ?? '' },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to fetch homepage products')
  }

  return response.json()
}

export const queryPrefix = 'homepage-products'

export function useHomepageFirstPageProductsQuery<TData = QData>(
  input: Input,
  options?: Omit<UseQueryOptions<QData, unknown, TData, QKey>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<QData, unknown, TData, QKey>({
    queryKey: [queryPrefix, input, 0] as const,
    queryFn: () =>
      fetchHomepageProducts({
        page: 0,
        pageSize: ITEMS_PER_PAGE,
        categories: input.categories,
        locale: input.locale,
        userId: input.userId,
      }),
    ...options,
  })
}

export function useInfiniteHomepageProductsQuery<TData = InfiniteData<QData, PageParam>>(
  input: Input,
  options?: Omit<
    UseInfiniteQueryOptions<QData, unknown, TData, QKeyInfiniteHomepageProducts, PageParam>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) {
  return useInfiniteQuery({
    queryKey: [queryPrefix, input, 'infinite'] as const,
    queryFn: ({ pageParam }: QueryFunctionContext<QKeyInfiniteHomepageProducts, PageParam>) => {
      return fetchHomepageProducts({
        ...(pageParam ?? { page: input.userId ? 0 : 1, pageSize: ITEMS_PER_PAGE }),
        categories: input.categories,
        locale: input.locale,
        userId: input.userId,
      })
    },
    getNextPageParam: (lastPage, pages) => {
      const lastPageIndex = pages.length
      const totalPages = Math.ceil(lastPage.totalProducts / ITEMS_PER_PAGE)
      if (totalPages <= lastPageIndex) {
        return
      }
      return { page: lastPageIndex + 1, pageSize: ITEMS_PER_PAGE }
    },
    initialPageParam: {
      page: input.userId ? 0 : 1,
      pageSize: ITEMS_PER_PAGE,
    },
    ...options,
  })
}
